import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchPublicaciones,
    Publicacion
} from '../../../services/blogPublicaciones';
import AddBlogPublicaciones from '../../../components/BlogPublicaciones/AddBlogPublicaciones';
import EditBlogPublicaciones from '../../../components/BlogPublicaciones/EditBlogPublicaciones';
import DeleteBlogPublicaciones from '../../../components/BlogPublicaciones/DeleteBlogPublicaciones';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const BlogPublicacionesPage: React.FC = () => {
    const [publicaciones, setPublicaciones] = useState<Publicacion[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPublicacion, setSelectedPublicacion] = useState<Publicacion | null>(null);

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('publicacionblog', userLevelId); // Ajusta el nombre del modelo según tus permisos

    const loadPublicaciones = useCallback(async () => {
        if (!perm?.can_read) {
            setPublicaciones([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchPublicaciones(); // Puedes agregar parámetros si deseas filtros iniciales
            setPublicaciones(data);
        } catch (error) {
            console.error('Error cargando publicaciones:', error);
        } finally {
            setLoading(false);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadPublicaciones();
    }, [loadPublicaciones]);

    const totalPages = Math.ceil(publicaciones.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedPublicaciones = publicaciones.slice(startIndex, startIndex + itemsPerPage);

    const handleRefresh = () => loadPublicaciones();

    const handleAddClick = () => setShowAddModal(true);

    const handleEditClick = (pub: Publicacion) => {
        setSelectedPublicacion(pub);
        setShowEditModal(true);
    };

    const handleDeleteClick = (pub: Publicacion) => {
        setSelectedPublicacion(pub);
        setShowDeleteModal(true);
    };

    const handleModalSuccess = () => loadPublicaciones();

    if (perm === null || (loading && publicaciones.length === 0)) return <Loading text="Cargando publicaciones..." />;

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Gestión de Publicaciones de Blogs</h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/blog-publicaciones" className="text-[#c1348b] hover:text-[#8f1f64]">Publicaciones de Blogs</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        onClick={handleRefresh}
                        style={{ height: '39px' }}
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center text-sm font-medium transition-colors"
                        title="Recargar Publicaciones"
                    >
                        <History className="w-5 h-5" />
                    </button>
                    {perm.can_create && (
                        <button
                            onClick={handleAddClick}
                            style={{ minWidth: '170.75px', height: '39px' }}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center text-sm font-medium transition-colors"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Publicación
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                {showAddModal && (
                    <AddBlogPublicaciones
                        isOpen={showAddModal}
                        onClose={() => setShowAddModal(false)}
                        onSuccess={handleModalSuccess}
                    />
                )}
                {showEditModal && selectedPublicacion && (
                    <EditBlogPublicaciones
                        isOpen={showEditModal}
                        publicacion={selectedPublicacion}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedPublicacion(null);
                        }}
                        onSuccess={handleModalSuccess}
                    />
                )}
                {showDeleteModal && selectedPublicacion && (
                    <DeleteBlogPublicaciones
                        isOpen={showDeleteModal}
                        publicacion={selectedPublicacion}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setSelectedPublicacion(null);
                        }}
                        onSuccess={handleModalSuccess}
                    />
                )}

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subtítulo</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Autor</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destacado</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Publicación</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                Cargando publicaciones...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : perm.can_read ? (
                                displayedPublicaciones.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-3">
                                                <Notebook className="w-12 h-12 text-gray-400" />
                                                <span className="text-lg font-medium text-gray-600">
                                                    No hay publicaciones disponibles
                                                </span>
                                                {perm.can_create && (
                                                    <p className="text-gray-500 text-sm">
                                                        Agrega una nueva publicación para comenzar
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    displayedPublicaciones.map((pub) => (
                                        <tr key={pub.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-sm text-gray-900 max-w-xs truncate">
                                                {pub.titulo}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{pub.slug}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500 max-w-xs truncate">
                                                {pub.subtitulo || '-'}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {pub.categoria?.nombre || 'Sin categoría'}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{pub.nombre_autor}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {pub.es_destacado ? (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                                        Destacado
                                                    </span>
                                                ) : (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                                        Normal
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                <span className={`px-2 inline-flex text-xs font-medium rounded-full ${
                                                    pub.estado === 'publicado'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {pub.estado === 'publicado' ? 'Publicado' : 'Borrador'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {pub.fecha_publicacion
                                                    ? new Date(pub.fecha_publicacion).toLocaleDateString()
                                                    : '-'}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex gap-2">
                                                    {perm.can_update && (
                                                        <button
                                                            onClick={() => handleEditClick(pub)}
                                                            className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                            title="Editar"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {perm.can_delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(pub)}
                                                            className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                            title="Eliminar"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )
                            ) : (
                                <tr>
                                    <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                                        No tienes permiso para ver las publicaciones.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && perm.can_read && publicaciones.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={publicaciones.length}
                    />
                )}
            </div>
        </div>
    );
};

export default BlogPublicacionesPage;